// LandingThankYouResultScreen — a Landing Varázsló DEDIKÁLT
// eredménykomponense a regisztrációs oldal 8-szekciós Szöveg nézetéhez.
//
// KERET (a Webinár Varázsló eredményképernyőjének mintáját követve, azzal
// pixelpontosan egyező szerkezetben):
//   • Felső agentsáv: PostHeader (importálva a MagnetPostFacebook-ból)
//     Beállítás / Webinár kapcsolóval; „Webinár" aktív.
//   • 440px + 1fr grid layout.
//   • BAL: sötét finomítópanel, alul chat input.
//   • JOBB: fejléc a 3 művelet gombbal (Teljes szöveg másolása / Letöltés
//     / Mentés a Tudástárba), alatta krémszínű munkaterület + egyetlen
//     fehér dokumentumkártya (width 100% a jobb területen belül).
//
// Semmilyen módon nem importál a `WebinarSectionCarousel`-ból, csak a
// `PostHeader` közös komponenst használja fel (a MagnetPostFacebook.js-t
// NEM módosítjuk).
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Copy, DownloadSimple, BookmarkSimple, PencilSimple,
  ArrowsCounterClockwise, CircleNotch, Calendar, CheckCircle,
  PaperPlaneTilt, Paperclip, X as XIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { PostHeader } from "./MagnetPostFacebook";
import { SuggestionCard, AppliedBlock } from "../components/AgentOutputScreen";

const GOLD_DEEP = "#8a6206";
const GOLD_LABEL = "#b5850a";
const GOLD_PRIMARY = "#eab308";
const GOLD_LIGHT_BG = "#fdf3d6";
const CREAM_WORK = "#f4ede2";
const CARD_BORDER = "#ece2c8";
const TEXT_DARK = "#221f19";
const TEXT_MID = "#4b4438";
const TEXT_BODY = "#3a3428";
const TEXT_SECOND = "#5c5648";
const TEXT_META = "#9b8f76";
const DIVIDER_START = "#f3d98a";
const DIVIDER_END = "#f9f2dd";

// A Webinár Varázsló CreamActionPill-jével PIXELPONTOSAN egyező arany
// pill háttér és keret. Ne módosítsd — a Webinár referenciájából származik.
const GOLD_BG = "rgba(255,219,92,0.14)";
const GOLD_BORDER = "rgba(255,219,92,0.36)";

// A Webinár Varázslóval IDENTIKUS 6-elemű quick-refine készlet.
const QUICK_REFINE_ACTIONS = [
  { id: "edge",     label: "Élesebb",     instruction: "Erősebb érzelmi feszültség, mélyebb fájdalom, konkrétabb jelenet, nagyobb hatás." },
  { id: "soft",     label: "Lágyabb",     instruction: "Melegebb, empatikusabb, barátságosabb, több megértés." },
  { id: "concrete", label: "Konkrétabb",  instruction: "Több konkrét részlet, időpont, hely, tárgy, érzés — semmi általánosítás." },
  { id: "story",    label: "Több sztori", instruction: "Cseréld ki vagy bővítsd ki egy rövid, életszagú személyes vagy hipotetikus jelenettel." },
  { id: "humor",    label: "Humorosabb",  instruction: "Adj bele finom öniróniát vagy könnyed humort, de maradj empatikus." },
  { id: "surprise", label: "Lepj meg!",   instruction: "Legyél kreatívabb, váratlanabb, erősebb hook vagy fordulat ebben a részben." },
];

/* ─── page ↔ text helpers ──────────────────────────────────────────
 * A `mod: "snippet"` esetnél a backend csak a kijelölt rész új
 * szöveges változatát adja vissza. Az `Alkalmazom` gomb az aktuális
 * `page` objektumban megkeresi a kijelölt idézetet bármelyik string
 * mezőben, és lecseréli az új snippetre. Az első előfordulást cseréli
 * — pontosan úgy, ahogy a WebinarSectionCarousel `applySnippetReplacement`
 * fallback-je a strukturált slide-fákon dolgozik.
 */
function _replaceInPageDeep(node, quote, snippet, state) {
  if (state.done) return node;
  if (typeof node === "string") {
    const idx = node.indexOf(quote);
    if (idx !== -1) {
      state.done = true;
      return node.slice(0, idx) + snippet + node.slice(idx + quote.length);
    }
    return node;
  }
  if (Array.isArray(node)) {
    return node.map((child) => _replaceInPageDeep(child, quote, snippet, state));
  }
  if (node && typeof node === "object") {
    const next = {};
    for (const k of Object.keys(node)) {
      next[k] = _replaceInPageDeep(node[k], quote, snippet, state);
    }
    return next;
  }
  return node;
}
function applyQuoteReplacement(page, quote, snippet) {
  const state = { done: false };
  const nextPage = _replaceInPageDeep(page, quote, snippet, state);
  return { page: nextPage, changed: state.done };
}

export default function LandingThankYouResultScreen({
  inputs, chosenTitle, initialPage, onBack, onSavedToVault,
}) {
  const [page, setPage] = useState(initialPage);
  const [savedName, setSavedName] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  // ── F3-mintás refine state machine (Webinár Varázslóval identikus) ──
  const [chatInput, setChatInput] = useState("");
  const [refinePhase, setRefinePhase] = useState("idle"); // idle | loading | suggestion | applied
  const [suggestion, setSuggestion] = useState(null);
  const [suggestionError, setSuggestionError] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [selection, setSelection] = useState(null);
  const [undoSnapshot, setUndoSnapshot] = useState(null);
  const undoTimerRef = useRef(null);
  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);

  // Az `initialPage` már a Köszönőoldal 6-szekciós sémáját használja
  // (`confirmation`, `calendar`, `next_steps`, `expectations`, `resource`,
  // `closing`). A `confirmation.webinar_name` és `confirmation.datetime`
  // a backend enforcementjén át MINDIG a forrás Regisztrációs oldal
  // `source_page.hero`-jából érkezik (Writer nem írja felül). Frontendben
  // pass-through — nincs további normalizálás.
  const _normalizeInitialPage = (raw) => raw;

  useEffect(() => { setPage(_normalizeInitialPage(initialPage)); }, [initialPage]);

  // ── Szövegkijelölés figyelése a fehér dokumentumkártyán belül ──
  useEffect(() => {
    const handler = () => {
      try {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const text = String(sel.toString() || "").trim();
        if (!text || text.length < 3) return;
        const anchor = sel.anchorNode?.parentElement?.closest?.('[data-testid="landing-thankyou-result-doc"]');
        const focus = sel.focusNode?.parentElement?.closest?.('[data-testid="landing-thankyou-result-doc"]');
        if (anchor && focus) setSelection(text);
      } catch { /* noop */ }
    };
    document.addEventListener("mouseup", handler);
    document.addEventListener("keyup", handler);
    return () => {
      document.removeEventListener("mouseup", handler);
      document.removeEventListener("keyup", handler);
    };
  }, []);

  useEffect(() => () => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  }, []);

  const updateSection = (sectionKey, newValue) =>
    setPage((p) => ({ ...p, [sectionKey]: newValue }));

  const clearSelection = () => {
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  const copyAll = async () => {
    const text = pageToPlainText(page);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Teljes szöveg vágólapra másolva");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Nem sikerült a másolás"); }
  };
  const downloadAll = () => {
    const text = pageToPlainText(page);
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `koszonooldal-${new Date().toISOString().slice(0,10)}.txt`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Letöltés elindítva");
  };
  const saveToVault = async () => {
    if (saving) return;
    const nameSuggestion = (chosenTitle?.webinar_name || page?.hero?.title || "Köszönőoldal").slice(0, 80);
    const name = window.prompt("Milyen néven mentsük a Tudástárba?", nameSuggestion);
    if (!name || !name.trim()) return;
    setSaving(true);
    try {
      const res = await api.post("/conversion/landing-thank-you/page/save", {
        name: name.trim(), page, chosen_title: chosenTitle || null, inputs: inputs || null,
      });
      if (res.data?.ok) {
        setSavedName(name.trim());
        toast.success("Mentve a Tudástárba");
        if (onSavedToVault) onSavedToVault();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mentés sikertelen");
    } finally { setSaving(false); }
  };

  /* ─── F3-mintás refine — közös adapter (WebinarSectionCarousel-lel identikus flow) ─ */

  const _callRefine = async (utasitas, quoteVal) =>
    api.post("/conversion/landing-thank-you/page/refine", {
      chosen_title: chosenTitle || {},
      webinar_cim: inputs?.webinar_cim || "",
      mit_tanuljon: inputs?.mit_tanuljon || "",
      miert_te: inputs?.miert_te || "",
      hova_hivjuk: inputs?.hova_hivjuk || "",
      current_page: page,
      user_instruction: utasitas || "",
      quote: quoteVal || null,
    });

  const requestRefine = async (instructionOverride, quickButtonId = null) => {
    const utasitas = ((instructionOverride && typeof instructionOverride === "string" ? instructionOverride : "") || chatInput).trim();
    if (!utasitas && !quickButtonId) return;
    if (refinePhase === "loading" || refinePhase === "suggestion") return;
    const quoteToSend = selection;
    // Ha quick-button jött, az utasítás a QUICK_REFINE_ACTIONS-ből
    const finalInstr = quickButtonId
      ? (QUICK_REFINE_ACTIONS.find((q) => q.id === quickButtonId)?.instruction || utasitas)
      : utasitas;
    setSuggestionError(false);
    setRefinePhase("loading");
    if (!instructionOverride && !quickButtonId) setChatInput("");
    try {
      const res = await _callRefine(finalInstr, quoteToSend);
      const data = res.data || {};
      setSuggestion({
        mod: data.mod === "snippet" ? "snippet" : "teljes",
        snippet: data.snippet || "",
        teljesPage: data.page || null,
        instr: finalInstr,
        quote: quoteToSend || null,
      });
      setRefinePhase("suggestion");
    } catch (e) {
      setSuggestion({ mod: "error", instr: finalInstr, quote: quoteToSend || null });
      setSuggestionError(true);
      setRefinePhase("suggestion");
    }
  };

  const retryVariant = async () => {
    if (!suggestion || regenerating) return;
    const previousSnippet = suggestion.mod === "snippet"
      ? (suggestion.snippet || "")
      : "";
    setRegenerating(true);
    setSuggestionError(false);
    try {
      const res = await _callRefine(
        (suggestion.instr || "")
          + "\n\nGenerálj egy érdemben MÁSIK változatot, mint az előzőt."
          + (previousSnippet ? ` Az előző javaslat ez volt: "${previousSnippet.slice(0, 280)}". Kerüld el az ismétlést.` : ""),
        suggestion.quote,
      );
      const data = res.data || {};
      setSuggestion((s) => ({
        ...s,
        snippet: data.snippet || "",
        teljesPage: data.page || null,
        mod: data.mod === "snippet" ? "snippet" : "teljes",
      }));
    } catch (e) {
      setSuggestionError(true);
    } finally {
      setRegenerating(false);
    }
  };

  const retryAfterError = () => {
    if (!suggestion) return;
    setSuggestionError(false);
    requestRefine(suggestion.instr);
  };

  const cancelSuggestion = () => {
    setSuggestion(null);
    setSuggestionError(false);
    setRefinePhase("idle");
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    const snapshot = JSON.parse(JSON.stringify(page));
    if (suggestion.mod === "snippet" && suggestion.snippet && suggestion.quote) {
      const { page: nextPage, changed } = applyQuoteReplacement(
        page, suggestion.quote, suggestion.snippet,
      );
      if (!changed) {
        toast.error("A kijelölt rész nem található már a szövegben.");
        return;
      }
      setUndoSnapshot(snapshot);
      setPage(nextPage);
    } else if (suggestion.mod === "teljes" && suggestion.teljesPage) {
      setUndoSnapshot(snapshot);
      // A teljes új page is átmegy a determinisztikus időpont-bekötésen
      // (a Writer által kitalált időpontot NEM használjuk).
      setPage(_normalizeInitialPage(suggestion.teljesPage));
    } else {
      return;
    }
    setSelection(null);
    window.getSelection()?.removeAllRanges();
    setTimeout(() => { try { window.getSelection()?.removeAllRanges(); } catch { /* noop */ } }, 0);
    setSuggestion(null);
    setRefinePhase("applied");
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => {
      setRefinePhase("idle");
      setUndoSnapshot(null);
    }, 8000);
  };

  const undoApplied = () => {
    if (!undoSnapshot) return;
    setPage(undoSnapshot);
    setUndoSnapshot(null);
    setRefinePhase("idle");
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  };

  const eventType = (page?.hero?.event_type || "webinár").trim() || "webinár";

  return (
    <div
      className="flex flex-col"
      style={{ height: "100vh", background: "#0a0a12", color: "#e8e8f0", overflow: "hidden" }}
      data-testid="landing-thankyou-result-shell"
    >
      {/* Felső agentsáv — PostHeader (Beállítás / Webinár kapcsoló) */}
      <PostHeader
        onBackToOpening={onBack}
        activeTab="tab2"
        onTabChange={(t) => { if (t === "tab1") onBack?.(); }}
        tab2Disabled={false}
        headerTitle="Landing Varázsló · Köszönőoldal"
        tab2Label="Köszönőoldal"
      />

      <div className="grid grid-cols-1 lg:grid-cols-[440px_1fr] flex-1 min-h-0 overflow-hidden">
        {/* ─── BAL — Finomító panel (WebinarSectionCarousel-lel pixelpontosan
             egyező elrendezés, állapotgép és „Javasolt változat" flow) ─── */}
        <div
          className="flex flex-col border-r min-h-0 h-full"
          style={{ borderColor: "rgba(255,255,255,0.04)" }}
          data-testid="landing-thankyou-result-finomito"
        >
          <div className="flex-1 overflow-y-auto" style={{ padding: "32px 28px 14px" }}>
            <div style={{ maxWidth: 480 }}>
              <div
                className="font-heading"
                style={{
                  fontSize: 28, fontWeight: 600, lineHeight: 1.3,
                  letterSpacing: "-0.015em", color: "#ffffff",
                  marginBottom: 18,
                }}
              >
                Elkészült a regisztrációs oldalad.
              </div>
              <div style={{
                fontSize: 16, fontWeight: 400, lineHeight: 1.7, color: "#c0c0d0",
              }}>
                Olvasd át a jobb oldalon a köszönőoldalt, szekcióról szekcióra.
                Ha valamit finomítanál, jelölj ki egy részt, és írd lent, mit
                változtassak. Ha minden rendben, mentsd el a Tudástárba.
              </div>
            </div>

            {/* Selection workspace */}
            <AnimatePresence>
              {(selection || refinePhase === "loading" || (refinePhase === "suggestion" && suggestion)) && (
                <motion.div
                  key="workspace"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    background: "rgba(124,108,242,0.025)",
                    border: "1px solid rgba(124,108,242,0.10)",
                    borderRadius: 12, padding: 14, margin: "32px -8px 0",
                  }}
                  data-testid="landing-thankyou-result-selection-workspace"
                >
                  {(selection || (suggestion && suggestion.quote)) && (
                    <div style={{
                      background: "rgba(255,255,255,0.04)",
                      borderLeft: "2px solid rgba(124,108,242,0.40)",
                      borderRadius: 8, padding: "11px 13px", marginBottom: 12,
                    }}>
                      <div className="flex items-start gap-2.5">
                        <div className="flex-1 min-w-0">
                          <div style={{
                            fontSize: 9, textTransform: "uppercase", letterSpacing: "0.16em",
                            color: "#8a8a9c", fontWeight: 600, marginBottom: 4,
                          }}>KIJELÖLT RÉSZ</div>
                          <div style={{
                            fontSize: 12.5, color: "#c0c0d0", lineHeight: 1.5,
                            display: "-webkit-box", WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical", overflow: "hidden",
                          }}>
                            {(() => {
                              const q = selection || suggestion?.quote || "";
                              return `„${q.length > 240 ? q.slice(0, 240) + "…" : q}"`;
                            })()}
                          </div>
                        </div>
                        {!(refinePhase === "suggestion" && suggestion) && refinePhase !== "loading" && (
                          <button
                            type="button"
                            onClick={clearSelection}
                            aria-label="Kijelölés törlése"
                            style={{
                              width: 22, height: 22, borderRadius: 5,
                              background: "rgba(255,255,255,0.04)",
                              border: "1px solid rgba(255,255,255,0.08)",
                              color: "#9898ad", cursor: "pointer",
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              flexShrink: 0,
                            }}
                            data-testid="landing-thankyou-result-selection-clear"
                          >
                            <XIcon size={11} weight="bold" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {refinePhase !== "loading" && !(refinePhase === "suggestion" && suggestion) && selection && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="landing-thankyou-result-quick-actions">
                      {QUICK_REFINE_ACTIONS.map((qa) => (
                        <button
                          key={qa.id}
                          type="button"
                          onClick={() => requestRefine(qa.instruction, qa.id)}
                          data-testid={`landing-thankyou-result-quick-${qa.id}`}
                          style={{
                            fontSize: 13, padding: "10px 14px", borderRadius: 999,
                            background: "rgba(124,108,242,0.10)",
                            border: "1px solid rgba(124,108,242,0.28)",
                            color: "#cfc8ff", cursor: "pointer",
                            fontFamily: "inherit", fontWeight: 500,
                            whiteSpace: "nowrap", textAlign: "center", width: "100%",
                          }}
                        >{qa.label}</button>
                      ))}
                    </div>
                  )}

                  {refinePhase === "loading" && (
                    <div
                      className="flex items-center gap-2.5"
                      style={{ fontSize: 13, color: "#a8a8c0", padding: "4px 2px" }}
                      data-testid="landing-thankyou-result-refine-loading"
                    >
                      <CircleNotch size={14} weight="bold" className="animate-spin" />
                      Finomítás folyamatban…
                    </div>
                  )}

                  {refinePhase === "suggestion" && suggestion && (
                    <div data-testid="landing-thankyou-result-suggestion">
                      <SuggestionCard
                        snippet={
                          suggestion.mod === "snippet"
                            ? (suggestion.snippet || "")
                            : (suggestion.teljesPage ? pageToPlainText(suggestion.teljesPage) : "")
                        }
                        isError={suggestionError}
                        regenerating={regenerating}
                        onApply={applySuggestion}
                        onRetryVariant={retryVariant}
                        onRetryAfterError={retryAfterError}
                        onCancel={cancelSuggestion}
                      />
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {refinePhase === "applied" && (
                <motion.div
                  key="applied"
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  style={{ margin: "24px 0 0" }}
                  data-testid="landing-thankyou-result-applied"
                >
                  <AppliedBlock onUndo={undoApplied} canUndo={!!undoSnapshot} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              const inputDisabled = refinePhase === "loading" || (refinePhase === "suggestion" && suggestion);
              if (chatInput.trim() && !inputDisabled) requestRefine();
            }}
            style={{ padding: "12px 24px 24px" }}
            data-testid="landing-thankyou-result-finomito-form"
          >
            {(() => {
              const isLoading = refinePhase === "loading";
              const showSuggestion = refinePhase === "suggestion" && suggestion;
              const inputDisabled = isLoading || showSuggestion;
              const placeholder = selection
                ? "Mit változtassak a kijelölt részen?"
                : "Jelölj ki egy részt jobboldalt, és írd ide, mit változtassak.";
              return (
                <>
                  <div style={{
                    background: "linear-gradient(180deg, rgba(243,238,228,0.06) 0%, rgba(243,238,228,0.02) 100%)",
                    border: "1px solid rgba(243,238,228,0.14)",
                    borderRadius: 12,
                    padding: "5px 5px 5px 14px",
                    display: "flex", alignItems: "center", gap: 10,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)",
                    opacity: inputDisabled ? 0.5 : 1,
                    pointerEvents: inputDisabled ? "none" : "auto",
                  }}>
                    <button
                      type="button"
                      aria-label="Csatolás"
                      style={{
                        background: "transparent", border: "none", padding: 0,
                        color: "#8a8a9c", cursor: "pointer",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      <Paperclip size={14} weight="regular" />
                    </button>
                    <input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          if (chatInput.trim() && !inputDisabled) requestRefine();
                        }
                      }}
                      placeholder={placeholder}
                      data-testid="landing-thankyou-result-chat-input"
                      disabled={inputDisabled}
                      className="flex-1 outline-none"
                      style={{
                        background: "transparent", border: "none", color: "white",
                        fontSize: 14, padding: "8px 0", fontFamily: "inherit",
                      }}
                    />
                    <button
                      type="submit"
                      disabled={!chatInput.trim() || inputDisabled}
                      data-testid="landing-thankyou-result-chat-send"
                      aria-label="Küldés"
                      style={{
                        width: 36, height: 36,
                        background: "linear-gradient(135deg, #a89bff, #7c6cf2)",
                        color: "white", borderRadius: 9, border: "none",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: chatInput.trim() && !inputDisabled ? "pointer" : "not-allowed",
                        opacity: chatInput.trim() && !inputDisabled ? 1 : 0.5,
                        boxShadow: chatInput.trim() && !inputDisabled
                          ? "0 4px 12px rgba(124,108,242,0.45), inset 0 0 0 1px rgba(255,255,255,0.10)"
                          : "none",
                      }}
                    >
                      {isLoading
                        ? <CircleNotch size={15} weight="bold" className="animate-spin" />
                        : <PaperPlaneTilt size={15} weight="fill" />}
                    </button>
                  </div>
                  <div className="flex justify-between" style={{ marginTop: 8, fontSize: 10.5, color: "#5a5a72" }}>
                    <span>Tipp: jelölj ki egy részt jobboldalt — a kérésed arra fog vonatkozni.</span>
                    <span>{isMac ? "⌘" : "Ctrl"} + Enter</span>
                  </div>
                </>
              );
            })()}
          </form>
        </div>

        {/* ─── JOBB — Eredményterület (krém wrapper: 3 gomb + fehér doc) ── */}
        <div className="h-full overflow-y-auto min-w-0" style={{ background: "#0a0a12" }}>
          <div style={{ padding: "26px" }}>
            <div
              style={{ background: CREAM_WORK, borderRadius: 16, padding: "22px 26px 26px" }}
              data-testid="landing-thankyou-result-cream"
            >
              {/* Fejléc gombok — PIXELPONTOSAN a Webinár Varázsló
                  `CreamActionPill`-je (arany háttér, arany keret, 999
                  border-radius, 12/500 tipó, 6px gap). Ne módosítsd a
                  komponenst — a Webinár referenciából származik. */}
              <div
                className="flex justify-end items-center flex-wrap"
                style={{ gap: 6, marginBottom: 20 }}
                data-testid="landing-thankyou-result-actions-bar"
              >
                <CreamActionPill
                  onClick={copyAll}
                  icon={copied ? CheckCircle : Copy}
                  label={copied ? "Másolva" : "Másolás"}
                  testid="landing-thankyou-result-copy-all"
                  active={copied}
                />
                <CreamActionPill
                  onClick={downloadAll}
                  icon={DownloadSimple}
                  label="Letöltés"
                  testid="landing-thankyou-result-download"
                />
                <CreamActionPill
                  onClick={saveToVault}
                  icon={savedName ? CheckCircle : BookmarkSimple}
                  label={saving ? "Mentés…" : savedName ? "Mentve" : "Mentés a Tudástárba"}
                  testid="landing-thankyou-result-save"
                  disabled={saving}
                  active={!!savedName}
                />
              </div>

              <motion.article
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28 }}
                style={{
                  background: "#ffffff", borderRadius: 20,
                  border: `0.5px solid ${CARD_BORDER}`,
                  boxShadow: "0 1px 3px rgba(60,50,20,0.05)",
                  padding: "56px 64px", width: "100%",
                }}
                className="landing-thankyou-result-doc"
                data-testid="landing-thankyou-result-doc"
              >
                <ConfirmationSection value={page.confirmation}
                  onChange={(v) => updateSection("confirmation", v)} />

                <Divider />
                <CalendarSection value={page.calendar}
                  onChange={(v) => updateSection("calendar", v)} />

                <Divider />
                <NextStepsSection value={page.next_steps}
                  onChange={(v) => updateSection("next_steps", v)} />

                <Divider />
                <ExpectationsSection value={page.expectations}
                  onChange={(v) => updateSection("expectations", v)} />

                {page.resource && (
                  <>
                    <Divider />
                    <ResourceSection value={page.resource}
                      onChange={(v) => updateSection("resource", v)} />
                  </>
                )}

                <Divider />
                <TyClosingSection value={page.closing}
                  onChange={(v) => updateSection("closing", v)} />
              </motion.article>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────── CreamActionPill — pixelpontosan a Webinár Varázslóból
     (`WebinarSectionCarousel.js` 488. sor). Ugyanaz a magasság, padding,
     border-radius, keret, betűméret és font-weight; disabled/active
     állapotok is identikusak. */
function CreamActionPill({ onClick, icon: Icon, label, testid, active, disabled }) {
  const bg = active ? "rgba(29,158,117,0.10)" : GOLD_BG;
  const border = active ? "rgba(29,158,117,0.30)" : GOLD_BORDER;
  const color = active ? "#1d9e75" : "#8a6d00";
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      data-testid={testid}
      disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 12px",
        background: bg, border: `1px solid ${border}`,
        borderRadius: 999, color,
        fontSize: 12, fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        whiteSpace: "nowrap",
        fontFamily: "inherit",
        transition: "all 180ms ease",
      }}
    >
      <Icon size={13} weight="regular" /> {label}
    </button>
  );
}

/* ───────── Divider ───────── */
function Divider() {
  return (
    <div style={{ height: 1, background: `linear-gradient(90deg, ${DIVIDER_START}, ${DIVIDER_END})`, margin: "44px 0 38px 0" }} aria-hidden="true" />
  );
}

/* ───────── Section actions (Copy / Edit / Regenerate) ───────── */
function SectionActions({ onCopy, onEdit, editActive, onRegenerate, regenerating, testidPrefix, showRegen = false }) {
  return (
    <div className="flex items-center gap-3" style={{ marginTop: 2 }}>
      <IconAction onClick={onCopy} icon={<Copy size={14} weight="regular" />} title="Másolás" testid={`${testidPrefix}-copy`} />
      <IconAction onClick={onEdit} icon={<PencilSimple size={14} weight={editActive ? "fill" : "regular"} />} title="Szerkesztés" testid={`${testidPrefix}-edit`} active={editActive} />
      {showRegen && (
        <IconAction
          onClick={onRegenerate} disabled={regenerating}
          icon={regenerating ? <CircleNotch size={14} weight="bold" className="animate-spin" /> : <ArrowsCounterClockwise size={14} weight="regular" />}
          title="Újragenerálás" testid={`${testidPrefix}-regen`}
        />
      )}
    </div>
  );
}
function IconAction({ onClick, icon, title, testid, active, disabled }) {
  return (
    <button
      type="button" onClick={onClick} title={title} disabled={disabled}
      data-testid={testid}
      className="inline-flex items-center transition"
      style={{
        background: "transparent", border: "none", padding: 4,
        color: active ? GOLD_DEEP : "#e2cf87",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.color = GOLD_DEEP; }}
      onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.color = active ? GOLD_DEEP : "#e2cf87"; }}
    >
      {icon}
    </button>
  );
}

/* ───────── HERO ───────── */
function HeroSection({ hero, eventType }) {
  const wn = (hero?.webinar_name || "").trim();
  const dt = (hero?.datetime || "").trim();
  return (
    <section data-testid="landing-thankyou-result-hero">
      <span style={{
        display: "inline-block", padding: "5px 12px", borderRadius: 999,
        background: GOLD_LIGHT_BG, color: GOLD_LABEL,
        fontFamily: "Inter, system-ui, sans-serif",
        fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", marginBottom: 20,
      }}>
        {(hero?.eyebrow || `INGYENES ÉLŐ ${eventType.toUpperCase()}`).trim()}
      </span>
      {/* Eseménynév + időpont egy közös metasorban (bal / jobb) */}
      <div className="flex items-baseline flex-wrap justify-between gap-x-6 gap-y-2" style={{ marginBottom: 14 }}>
        {wn ? (
          <div className="font-heading" data-testid="landing-thankyou-result-hero-name"
            style={{ fontSize: 22, color: GOLD_DEEP, fontWeight: 500, lineHeight: 1.35, letterSpacing: "-0.005em" }}>
            {wn}
          </div>
        ) : <span />}
        <div className="inline-flex items-center gap-1.5" data-testid="landing-thankyou-result-hero-datetime"
          style={{ fontSize: 13, color: "#8a7f68", fontFamily: "Inter, system-ui, sans-serif" }}>
          <Calendar size={15} color={GOLD_LABEL} weight="regular" />
          {dt || <em style={{ color: TEXT_META, fontStyle: "italic" }}>Add meg az esemény időpontját</em>}
        </div>
      </div>
      <h1 className="font-heading" data-testid="landing-thankyou-result-hero-title"
        style={{ fontSize: 31, color: TEXT_DARK, fontWeight: 500, lineHeight: 1.32, marginBottom: 18, letterSpacing: "-0.015em" }}>
        {hero?.title || ""}
      </h1>
      <p style={{ fontSize: 16, color: TEXT_SECOND, lineHeight: 1.7, marginBottom: 24 }} data-testid="landing-thankyou-result-hero-subtitle">
        {hero?.subtitle || ""}
      </p>
      <button type="button" data-testid="landing-thankyou-result-hero-cta"
        style={{
          background: "linear-gradient(180deg, #ffdb5c, #eab308)", color: "#4a3400",
          border: "none", borderRadius: 999, padding: "14px 28px",
          fontSize: 14, fontWeight: 600, cursor: "pointer",
          fontFamily: "Inter, system-ui, sans-serif",
          boxShadow: "0 4px 12px rgba(234,179,8,0.25)",
        }}>
        {hero?.cta || "Kérem az ingyenes helyem"}
      </button>
      {(hero?.microcopy || "").trim() && (
        <p style={{ fontSize: 12, color: TEXT_META, marginTop: 10 }} data-testid="landing-thankyou-result-hero-microcopy">
          {hero.microcopy}
        </p>
      )}
    </section>
  );
}

/* ───────── TEACHINGS ───────── */
function TeachingsSection({ value, onChange, regenPayload, onRegenerated }) {
  const [editing, setEditing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const items = value?.items || [];
  const doCopy = async () => {
    const t = `${value?.title || ""}\n\n${items.map((it) => `${it.number}. ${it.text}`).join("\n\n")}`;
    try { await navigator.clipboard.writeText(t); toast.success("Szekció másolva"); } catch { toast.error("Másolás sikertelen"); }
  };
  const doRegen = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      const res = await api.post("/conversion/landing-thank-you/page/section/regenerate", regenPayload);
      if (res.data?.section) { onRegenerated(res.data.section); toast.success("Szekció újragenerálva"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Újragenerálás sikertelen"); }
    finally { setRegenerating(false); }
  };
  return (
    <section data-testid="landing-thankyou-result-teachings">
      <SectionHeader title={value?.title || "Ezt a három dolgot viszed magaddal"}>
        <SectionActions onCopy={doCopy} onEdit={() => setEditing((e) => !e)} editActive={editing}
          onRegenerate={doRegen} regenerating={regenerating} testidPrefix="landing-thankyou-result-teachings" />
      </SectionHeader>
      <div style={{ marginTop: 22 }}>
        {items.map((it, idx) => (
          <div key={idx} className="flex items-start" style={{ gap: 14, marginBottom: 18 }}>
            <div style={{
              width: 26, height: 26, flexShrink: 0, borderRadius: "50%",
              background: "linear-gradient(180deg, #ffdb5c, #eab308)",
              color: "#ffffff", fontFamily: "Satoshi, system-ui, sans-serif",
              fontSize: 14, fontWeight: 500,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 2px 5px rgba(234,179,8,0.25)",
            }}>{it.number}</div>
            <EditableInline value={it.text} editing={editing}
              onChange={(newText) => onChange({ ...value, items: items.map((x, i) => i === idx ? { ...x, text: newText } : x) })}
              style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_BODY }} multiline
              testid={`landing-thankyou-result-teachings-item-${idx}`} />
          </div>
        ))}
      </div>
    </section>
  );
}

/* ───────── EMPATHY ───────── */
function EmpathySection({ value, onChange, regenPayload, onRegenerated }) {
  const [editing, setEditing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const body = value?.body || [];
  const doCopy = async () => {
    const t = `${value?.title || ""}\n\n${body.join("\n\n")}`;
    try { await navigator.clipboard.writeText(t); toast.success("Szekció másolva"); } catch { toast.error("Másolás sikertelen"); }
  };
  const doRegen = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      const res = await api.post("/conversion/landing-thank-you/page/section/regenerate", regenPayload);
      if (res.data?.section) { onRegenerated(res.data.section); toast.success("Szekció újragenerálva"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Újragenerálás sikertelen"); }
    finally { setRegenerating(false); }
  };
  return (
    <section data-testid="landing-thankyou-result-empathy">
      <SectionHeader title={value?.title || "Lehet, hogy most te is itt tartasz…"}>
        <SectionActions onCopy={doCopy} onEdit={() => setEditing((e) => !e)} editActive={editing}
          onRegenerate={doRegen} regenerating={regenerating} testidPrefix="landing-thankyou-result-empathy" />
      </SectionHeader>
      <div style={{ marginTop: 18 }}>
        {body.map((p, i) => (
          <EditableInline key={i} value={p} editing={editing}
            onChange={(nv) => onChange({ ...value, body: body.map((x, j) => j === i ? nv : x) })}
            style={{ fontSize: 15, lineHeight: 1.75, color: TEXT_BODY, marginBottom: 14 }} multiline
            testid={`landing-thankyou-result-empathy-p-${i}`} />
        ))}
      </div>
    </section>
  );
}

/* ───────── FOR YOU ───────── */
function ForYouSection({ value, eventType, onChange, regenPayload, onRegenerated }) {
  const [editing, setEditing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const items = value?.items || [];
  const doCopy = async () => {
    const t = `${value?.title || ""}\n\n${items.map((it) => `- ${it}`).join("\n")}`;
    try { await navigator.clipboard.writeText(t); toast.success("Szekció másolva"); } catch { toast.error("Másolás sikertelen"); }
  };
  const doRegen = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      const res = await api.post("/conversion/landing-thank-you/page/section/regenerate", regenPayload);
      if (res.data?.section) { onRegenerated(res.data.section); toast.success("Szekció újragenerálva"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Újragenerálás sikertelen"); }
    finally { setRegenerating(false); }
  };
  return (
    <section data-testid="landing-thankyou-result-foryou">
      <SectionHeader title={value?.title || `Neked szól ez a ${eventType}, ha…`}>
        <SectionActions onCopy={doCopy} onEdit={() => setEditing((e) => !e)} editActive={editing}
          onRegenerate={doRegen} regenerating={regenerating} testidPrefix="landing-thankyou-result-foryou" />
      </SectionHeader>
      <div style={{ marginTop: 18 }}>
        {items.map((it, i) => (
          <div key={i} className="flex items-start" style={{ gap: 10, marginBottom: 10 }}>
            <CheckCircle size={18} weight="fill" color={GOLD_PRIMARY} style={{ flexShrink: 0, marginTop: 2 }} />
            <EditableInline value={it} editing={editing}
              onChange={(nv) => onChange({ ...value, items: items.map((x, j) => j === i ? nv : x) })}
              style={{ fontSize: 15, lineHeight: 1.65, color: TEXT_BODY }}
              testid={`landing-thankyou-result-foryou-item-${i}`} />
          </div>
        ))}
      </div>
    </section>
  );
}

/* ───────── DIFFERENCE ───────── */
function DifferenceSection({ value, eventType, onChange, regenPayload, onRegenerated }) {
  const [editing, setEditing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const doCopy = async () => {
    const t = `${value?.title || ""}\n\n${value?.highlight || ""}\n\n${value?.body || ""}`;
    try { await navigator.clipboard.writeText(t); toast.success("Szekció másolva"); } catch { toast.error("Másolás sikertelen"); }
  };
  const doRegen = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      const res = await api.post("/conversion/landing-thank-you/page/section/regenerate", regenPayload);
      if (res.data?.section) { onRegenerated(res.data.section); toast.success("Szekció újragenerálva"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Újragenerálás sikertelen"); }
    finally { setRegenerating(false); }
  };
  return (
    <section data-testid="landing-thankyou-result-diff">
      <SectionHeader title={value?.title || `Miért más ez a ${eventType}?`}>
        <SectionActions onCopy={doCopy} onEdit={() => setEditing((e) => !e)} editActive={editing}
          onRegenerate={doRegen} regenerating={regenerating} testidPrefix="landing-thankyou-result-diff" />
      </SectionHeader>
      <div style={{ marginTop: 18 }}>
        <EditableInline value={value?.highlight || ""} editing={editing}
          onChange={(nv) => onChange({ ...value, highlight: nv })}
          style={{ fontFamily: "Satoshi, system-ui, sans-serif", fontSize: 16, fontWeight: 500, color: GOLD_DEEP, marginBottom: 10, lineHeight: 1.5 }}
          multiline testid="landing-thankyou-result-diff-highlight" />
        <EditableInline value={value?.body || ""} editing={editing}
          onChange={(nv) => onChange({ ...value, body: nv })}
          style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_BODY }} multiline
          testid="landing-thankyou-result-diff-body" />
      </div>
    </section>
  );
}

/* ───────── SPEAKER ───────── */
function SpeakerSection({ value, onChange, regenPayload, onRegenerated }) {
  const [editing, setEditing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const doCopy = async () => {
    const t = `${value?.title || ""}\n\n${value?.name ? value.name + "\n\n" : ""}${value?.bio || ""}`;
    try { await navigator.clipboard.writeText(t); toast.success("Szekció másolva"); } catch { toast.error("Másolás sikertelen"); }
  };
  const doRegen = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      const res = await api.post("/conversion/landing-thank-you/page/section/regenerate", regenPayload);
      if (res.data?.section) { onRegenerated(res.data.section); toast.success("Szekció újragenerálva"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Újragenerálás sikertelen"); }
    finally { setRegenerating(false); }
  };
  return (
    <section data-testid="landing-thankyou-result-speaker">
      <SectionHeader title={value?.title || "Néhány szó rólam"}>
        <SectionActions onCopy={doCopy} onEdit={() => setEditing((e) => !e)} editActive={editing}
          onRegenerate={doRegen} regenerating={regenerating} testidPrefix="landing-thankyou-result-speaker" />
      </SectionHeader>
      <div style={{ marginTop: 18 }}>
        {(value?.name || editing) && (
          <EditableInline value={value?.name || ""} editing={editing}
            onChange={(nv) => onChange({ ...value, name: nv })}
            style={{ fontFamily: "Satoshi, system-ui, sans-serif", fontSize: 17, fontWeight: 500, color: TEXT_DARK, marginBottom: 10 }}
            testid="landing-thankyou-result-speaker-name" />
        )}
        <EditableInline value={value?.bio || ""} editing={editing}
          onChange={(nv) => onChange({ ...value, bio: nv })}
          style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_BODY }} multiline
          testid="landing-thankyou-result-speaker-bio" />
      </div>
    </section>
  );
}

/* ───────── EVENT ───────── */
function EventSection({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const doCopy = async () => {
    const t = `${value?.title || ""}\n\nDátum: ${value?.date || ""}\nIdőpont: ${value?.time || ""}\nForma: ${value?.format || ""}\nRészvétel: ${value?.price || ""}\n\n${value?.cta || ""}\n${value?.microcopy || ""}`;
    try { await navigator.clipboard.writeText(t); toast.success("Szekció másolva"); } catch { toast.error("Másolás sikertelen"); }
  };
  // Ha `date` szabad szöveg (nem tartalmaz csak számokat / dátumszavakat)
  // és a `time` üres, VAGY ha `date` értéke ténylegesen kombinált szöveg,
  // akkor összevont „Dátum és időpont" mezőt mutatunk.
  const dateVal = (value?.date || "").trim();
  const timeVal = (value?.time || "").trim();
  const useCombined = dateVal && !timeVal;
  return (
    <section data-testid="landing-thankyou-result-event">
      <SectionHeader title={value?.title || "Időpont és részvétel"}>
        <SectionActions onCopy={doCopy} onEdit={() => setEditing((e) => !e)} editActive={editing}
          testidPrefix="landing-thankyou-result-event" showRegen={false} />
      </SectionHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" style={{ gap: 22, marginTop: 22 }}>
        {useCombined ? (
          <EventField
            label="Dátum és időpont" value={dateVal} editing={editing}
            onChange={(v) => onChange({ ...value, date: v })}
            placeholder="Add meg az esemény időpontját"
            testid="landing-thankyou-result-event-date"
            spanTwo
          />
        ) : (
          <>
            <EventField label="Dátum" value={dateVal} editing={editing}
              onChange={(v) => onChange({ ...value, date: v })}
              placeholder="Add meg a dátumot" testid="landing-thankyou-result-event-date" />
            <EventField label="Időpont" value={timeVal} editing={editing}
              onChange={(v) => onChange({ ...value, time: v })}
              placeholder="Add meg az időpontot" testid="landing-thankyou-result-event-time" />
          </>
        )}
        <EventField label="Forma" value={value?.format || ""} editing={editing} onChange={(v) => onChange({ ...value, format: v })} placeholder="Online" testid="landing-thankyou-result-event-format" />
        <EventField label="Részvétel" value={value?.price || ""} editing={editing} onChange={(v) => onChange({ ...value, price: v })} placeholder="Ingyenes" testid="landing-thankyou-result-event-price" />
      </div>
      {(value?.microcopy || "").trim() && (
        <p style={{ fontSize: 12, color: TEXT_META, marginTop: 16 }}>{value.microcopy}</p>
      )}
    </section>
  );
}
function EventField({ label, value, editing, onChange, placeholder, testid, spanTwo }) {
  return (
    <div style={spanTwo ? { gridColumn: "span 2" } : undefined}>
      <div style={{ fontFamily: "Inter, system-ui, sans-serif", fontSize: 12, fontWeight: 600, color: GOLD_LABEL, marginBottom: 6, letterSpacing: "0.02em" }}>{label}</div>
      {editing ? (
        <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
          style={{ width: "100%", background: "transparent", border: `1px dashed ${DIVIDER_START}`, borderRadius: 6, padding: "6px 8px", fontSize: 14, color: TEXT_BODY, fontFamily: "inherit" }} />
      ) : (
        <div style={{ fontSize: 14, color: value ? TEXT_BODY : TEXT_META, fontStyle: value ? "normal" : "italic" }} data-testid={testid}>
          {value || placeholder}
        </div>
      )}
    </div>
  );
}

/* ───────── CLOSING ───────── */
function ClosingSection({ value, onChange, regenPayload, onRegenerated }) {
  const [editing, setEditing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const doCopy = async () => {
    const t = `${value?.title || ""}\n\n${value?.body || ""}\n\n${value?.cta || ""}`;
    try { await navigator.clipboard.writeText(t); toast.success("Szekció másolva"); } catch { toast.error("Másolás sikertelen"); }
  };
  const doRegen = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      const res = await api.post("/conversion/landing-thank-you/page/section/regenerate", regenPayload);
      if (res.data?.section) { onRegenerated(res.data.section); toast.success("Szekció újragenerálva"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Újragenerálás sikertelen"); }
    finally { setRegenerating(false); }
  };
  return (
    <section data-testid="landing-thankyou-result-closing">
      <SectionHeader title={value?.title || ""}>
        <SectionActions onCopy={doCopy} onEdit={() => setEditing((e) => !e)} editActive={editing}
          onRegenerate={doRegen} regenerating={regenerating} testidPrefix="landing-thankyou-result-closing" />
      </SectionHeader>
      <div style={{ marginTop: 18 }}>
        <EditableInline value={value?.body || ""} editing={editing}
          onChange={(nv) => onChange({ ...value, body: nv })}
          style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_BODY, marginBottom: 20 }} multiline
          testid="landing-thankyou-result-closing-body" />
        <button type="button" data-testid="landing-thankyou-result-closing-cta"
          style={{
            background: "linear-gradient(180deg, #ffdb5c, #eab308)", color: "#4a3400",
            border: "none", borderRadius: 999, padding: "14px 28px",
            fontSize: 14, fontWeight: 600, cursor: "pointer",
            fontFamily: "Inter, system-ui, sans-serif",
            boxShadow: "0 4px 12px rgba(234,179,8,0.25)",
          }}>
          {value?.cta || "Kérem az ingyenes helyem"}
        </button>
      </div>
    </section>
  );
}

/* ───────── Section header ───────── */
/* ═══════════════════════════════════════════════════════════════════
   KÖSZÖNŐOLDAL v2 SZEKCIÓK — 6 új blokk, ugyanazzal a stílussal, mint
   a Regisztrációs szekciók (SectionHeader / EditableInline / Divider /
   SectionActions).
   ═══════════════════════════════════════════════════════════════════ */

// 1) Sikeres regisztráció (Hero) — eyebrow-pill, webinar_name + datetime
//    egy sorban a Regisztrációs Hero mintája szerint, cím, megerősítő szöveg.
function ConfirmationSection({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const v = value || {};
  const eyebrow = (v.eyebrow || "SIKERES REGISZTRÁCIÓ").trim();
  const wn = (v.webinar_name || "").trim();
  const dt = (v.datetime || "").trim();
  const patch = (k, val) => onChange({ ...v, [k]: val });
  const copySection = async () => {
    const text = [eyebrow, [wn, dt].filter(Boolean).join(" · "), v.title || "", "", v.body || ""].join("\n");
    try { await navigator.clipboard.writeText(text.trim()); toast.success("Szekció másolva"); } catch { /* noop */ }
  };
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Eyebrow-pill — pixelpontosan a Regisztrációs Hero eyebrow-jából */}
          <div style={{
            display: "inline-block",
            fontSize: 10.5, letterSpacing: "0.16em", textTransform: "uppercase",
            color: "#8a6d00", fontWeight: 600,
            background: "rgba(255,219,92,0.14)",
            border: "1px solid rgba(255,219,92,0.36)",
            borderRadius: 999, padding: "4px 12px",
            marginBottom: 10,
          }}>{eyebrow}</div>

          {(wn || dt) && (
            <div style={{ fontSize: 13, color: "#7a7060", marginBottom: 8 }}>
              {[wn, dt].filter(Boolean).join(" · ")}
            </div>
          )}

          <EditableInline
            value={v.title} editing={editing}
            onChange={(nv) => patch("title", nv)}
            testid="landing-thankyou-result-confirmation-title"
            style={{
              fontSize: 30, fontWeight: 600, lineHeight: 1.25,
              color: TEXT_DARK, letterSpacing: "-0.015em",
              marginBottom: 12,
            }} />

          <EditableInline
            value={v.body} editing={editing} multiline
            onChange={(nv) => patch("body", nv)}
            testid="landing-thankyou-result-confirmation-body"
            style={{ fontSize: 15, lineHeight: 1.65, color: TEXT_MID }} />
        </div>
        <SectionActions onCopy={copySection} onEdit={() => setEditing((e) => !e)}
          editActive={editing} showRegen={false} />
      </div>
    </section>
  );
}

// 2) Add hozzá a naptáradhoz — 3 gomb-felirat, aktuális fázisban csak
//    labelek jelennek meg (url = null, gombok kattinthatatlanok).
function CalendarSection({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const v = value || {};
  const title = (v.title || "Add hozzá a naptáradhoz").trim();
  const buttons = Array.isArray(v.buttons) ? v.buttons : [];
  const patch = (k, val) => onChange({ ...v, [k]: val });
  const copySection = async () => {
    const btnLine = buttons.map((b) => b?.label).filter(Boolean).join("  ·  ");
    try {
      await navigator.clipboard.writeText([title, v.body || "", btnLine].filter(Boolean).join("\n"));
      toast.success("Szekció másolva");
    } catch { /* noop */ }
  };
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <SectionHeader title={title}>
        <SectionActions onCopy={copySection} onEdit={() => setEditing((e) => !e)}
          editActive={editing} showRegen={false} />
      </SectionHeader>
      <EditableInline value={v.body} editing={editing} multiline
        onChange={(nv) => patch("body", nv)}
        testid="landing-thankyou-result-calendar-body"
        style={{ fontSize: 15, lineHeight: 1.65, color: TEXT_MID }} />
      <div className="flex flex-wrap" style={{ gap: 10, marginTop: 6 }}>
        {buttons.map((b, i) => (
          <span key={i}
            data-testid={`landing-thankyou-result-calendar-btn-${i}`}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "8px 16px", borderRadius: 999,
              background: "rgba(255,219,92,0.14)",
              border: "1px solid rgba(255,219,92,0.36)",
              color: "#8a6d00", fontSize: 13, fontWeight: 500,
              fontFamily: "inherit",
            }}>{b?.label || ""}</span>
        ))}
      </div>
    </section>
  );
}

// 3) Következő lépések — 3 számozott elem
function NextStepsSection({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const v = value || {};
  const title = (v.title || "Következő lépések").trim();
  const items = Array.isArray(v.items) ? v.items : [];
  const patchItem = (idx, key, val) => {
    const next = items.slice();
    next[idx] = { ...(next[idx] || {}), [key]: val };
    onChange({ ...v, items: next });
  };
  const copySection = async () => {
    const lines = [title, ""];
    items.forEach((it, i) => {
      lines.push(`${it?.number ?? i + 1}. ${it?.title || ""}`);
      if (it?.body) lines.push(it.body);
      lines.push("");
    });
    try { await navigator.clipboard.writeText(lines.join("\n").trim()); toast.success("Szekció másolva"); } catch { /* noop */ }
  };
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <SectionHeader title={title}>
        <SectionActions onCopy={copySection} onEdit={() => setEditing((e) => !e)}
          editActive={editing} showRegen={false} />
      </SectionHeader>
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {items.map((it, idx) => (
          <div key={idx} className="flex items-start gap-4">
            <div style={{
              flexShrink: 0, width: 32, height: 32, borderRadius: 999,
              background: "rgba(255,219,92,0.14)",
              border: "1px solid rgba(255,219,92,0.36)",
              color: "#8a6d00", fontWeight: 600, fontSize: 14,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              marginTop: 2,
            }}>{it?.number ?? idx + 1}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <EditableInline value={it?.title || ""} editing={editing}
                onChange={(nv) => patchItem(idx, "title", nv)}
                testid={`landing-thankyou-result-nextstep-${idx}-title`}
                style={{ fontSize: 17, fontWeight: 600, color: TEXT_DARK, marginBottom: 6, lineHeight: 1.35 }} />
              <EditableInline value={it?.body || ""} editing={editing} multiline
                onChange={(nv) => patchItem(idx, "body", nv)}
                testid={`landing-thankyou-result-nextstep-${idx}-body`}
                style={{ fontSize: 14.5, lineHeight: 1.6, color: TEXT_MID }} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// 4) Mire számíthatsz? — 3 elem cím+body
function ExpectationsSection({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const v = value || {};
  const title = (v.title || "Mire számíthatsz?").trim();
  const items = Array.isArray(v.items) ? v.items : [];
  const patchItem = (idx, key, val) => {
    const next = items.slice();
    next[idx] = { ...(next[idx] || {}), [key]: val };
    onChange({ ...v, items: next });
  };
  const copySection = async () => {
    const lines = [title, ""];
    items.forEach((it) => {
      lines.push(it?.title || "");
      if (it?.body) lines.push(it.body);
      lines.push("");
    });
    try { await navigator.clipboard.writeText(lines.join("\n").trim()); toast.success("Szekció másolva"); } catch { /* noop */ }
  };
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <SectionHeader title={title}>
        <SectionActions onCopy={copySection} onEdit={() => setEditing((e) => !e)}
          editActive={editing} showRegen={false} />
      </SectionHeader>
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {items.map((it, idx) => (
          <div key={idx}>
            <EditableInline value={it?.title || ""} editing={editing}
              onChange={(nv) => patchItem(idx, "title", nv)}
              testid={`landing-thankyou-result-expect-${idx}-title`}
              style={{ fontSize: 16, fontWeight: 600, color: TEXT_DARK, marginBottom: 6, lineHeight: 1.4 }} />
            <EditableInline value={it?.body || ""} editing={editing} multiline
              onChange={(nv) => patchItem(idx, "body", nv)}
              testid={`landing-thankyou-result-expect-${idx}-body`}
              style={{ fontSize: 14.5, lineHeight: 1.6, color: TEXT_MID }} />
          </div>
        ))}
      </div>
    </section>
  );
}

// 5) Opcionális letölthető anyag / felkészülés
function ResourceSection({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const v = value || {};
  const title = (v.title || "").trim();
  const patch = (k, val) => onChange({ ...v, [k]: val });
  const copySection = async () => {
    const line = [title, v.body || "", v.cta ? `${v.cta}${v.url ? ` — ${v.url}` : ""}` : ""].filter(Boolean).join("\n");
    try { await navigator.clipboard.writeText(line); toast.success("Szekció másolva"); } catch { /* noop */ }
  };
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <SectionHeader title={title || "Letölthető anyag"}>
        <SectionActions onCopy={copySection} onEdit={() => setEditing((e) => !e)}
          editActive={editing} showRegen={false} />
      </SectionHeader>
      <EditableInline value={v.body} editing={editing} multiline
        onChange={(nv) => patch("body", nv)}
        testid="landing-thankyou-result-resource-body"
        style={{ fontSize: 15, lineHeight: 1.65, color: TEXT_MID }} />
      {(v.cta || v.url) && (
        <div style={{ marginTop: 4 }}>
          {v.url ? (
            <a href={v.url} target="_blank" rel="noreferrer"
              data-testid="landing-thankyou-result-resource-cta"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "10px 18px", borderRadius: 999,
                background: "rgba(255,219,92,0.14)",
                border: "1px solid rgba(255,219,92,0.36)",
                color: "#8a6d00", fontSize: 13.5, fontWeight: 500,
                fontFamily: "inherit", textDecoration: "none",
              }}>{v.cta || "Letöltés"}</a>
          ) : (
            <span
              data-testid="landing-thankyou-result-resource-cta"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "10px 18px", borderRadius: 999,
                background: "rgba(255,219,92,0.14)",
                border: "1px solid rgba(255,219,92,0.36)",
                color: "#8a6d00", fontSize: 13.5, fontWeight: 500,
                fontFamily: "inherit",
              }}>{v.cta || "Letöltés"}</span>
          )}
        </div>
      )}
    </section>
  );
}

// 6) Záró megerősítés
function TyClosingSection({ value, onChange }) {
  const [editing, setEditing] = useState(false);
  const v = value || {};
  const patch = (k, val) => onChange({ ...v, [k]: val });
  const copySection = async () => {
    try { await navigator.clipboard.writeText([v.title || "", v.body || ""].join("\n").trim()); toast.success("Szekció másolva"); } catch { /* noop */ }
  };
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <SectionHeader title={v.title || "Záró megerősítés"}>
        <SectionActions onCopy={copySection} onEdit={() => setEditing((e) => !e)}
          editActive={editing} showRegen={false} />
      </SectionHeader>
      <EditableInline value={v.body} editing={editing} multiline
        onChange={(nv) => patch("body", nv)}
        testid="landing-thankyou-result-closing-body"
        style={{ fontSize: 15, lineHeight: 1.65, color: TEXT_MID }} />
    </section>
  );
}

function SectionHeader({ title, children }) {
  return (
    <header className="flex items-start justify-between flex-wrap gap-4">
      <h2 className="font-heading"
        style={{ fontSize: 19, color: TEXT_DARK, fontWeight: 500, lineHeight: 1.4, letterSpacing: "-0.01em", flex: 1, minWidth: 0 }}>
        {title}
      </h2>
      {children}
    </header>
  );
}

/* ───────── Editable inline (contentEditable-like) ───────── */
function EditableInline({ value, editing, onChange, style, multiline, testid }) {
  const ref = useRef(null);
  useEffect(() => {
    if (editing && ref.current && ref.current.innerText !== value) {
      ref.current.innerText = value || "";
    }
  }, [editing, value]);
  if (!editing) {
    return <div style={style} data-testid={testid}>{value}</div>;
  }
  return (
    <div ref={ref} contentEditable suppressContentEditableWarning role="textbox"
      onBlur={(e) => onChange(e.currentTarget.innerText.replace(/\u00A0/g, " "))}
      data-testid={testid}
      style={{
        ...style, outline: "none",
        background: "rgba(234,179,8,0.06)",
        border: `1px dashed ${DIVIDER_START}`,
        borderRadius: 6, padding: "4px 8px",
        minHeight: multiline ? 32 : 24,
        whiteSpace: "pre-wrap",
      }}>
      {value}
    </div>
  );
}

/* ───────── Helpers ───────── */
function buildRegenPayload(sectionId, inputs, chosenTitle, page) {
  return {
    section_id: sectionId,
    chosen_title: chosenTitle || {},
    webinar_cim: inputs?.webinar_cim || "",
    mit_tanuljon: inputs?.mit_tanuljon || "",
    miert_te: inputs?.miert_te || "",
    hova_hivjuk: inputs?.hova_hivjuk || "",
    current_page: page || {},
  };
}

function pageToPlainText(page) {
  if (!page) return "";
  const p = page;
  const lines = [];
  lines.push("KÖSZÖNŐOLDAL");
  lines.push("");

  const conf = p.confirmation || {};
  lines.push((conf.eyebrow || "SIKERES REGISZTRÁCIÓ"));
  const metaLine = [conf.webinar_name, conf.datetime].filter(Boolean).join(" · ");
  if (metaLine) lines.push(metaLine);
  lines.push("");
  if (conf.title) { lines.push(conf.title); lines.push(""); }
  if (conf.body) { lines.push(conf.body); lines.push(""); }

  const cal = p.calendar || {};
  if (cal.title || cal.body || (cal.buttons || []).length) {
    lines.push((cal.title || "Add hozzá a naptáradhoz").toUpperCase());
    if (cal.body) lines.push(cal.body);
    if ((cal.buttons || []).length) {
      lines.push("");
      lines.push((cal.buttons || []).map((b) => b?.label).filter(Boolean).join("  ·  "));
    }
    lines.push("");
  }

  const ns = p.next_steps || {};
  if ((ns.items || []).length) {
    lines.push((ns.title || "Következő lépések").toUpperCase());
    (ns.items || []).forEach((it, i) => {
      lines.push(`${it?.number ?? i + 1}. ${it?.title || ""}`);
      if (it?.body) lines.push(it.body);
      lines.push("");
    });
  }

  const exp = p.expectations || {};
  if ((exp.items || []).length) {
    lines.push((exp.title || "Mire számíthatsz?").toUpperCase());
    (exp.items || []).forEach((it) => {
      if (it?.title) lines.push(it.title);
      if (it?.body) lines.push(it.body);
      lines.push("");
    });
  }

  if (p.resource) {
    const r = p.resource;
    lines.push((r.title || "LETÖLTHETŐ ANYAG").toUpperCase());
    if (r.body) lines.push(r.body);
    if (r.cta || r.url) {
      lines.push([r.cta, r.url].filter(Boolean).join(" — "));
    }
    lines.push("");
  }

  const clo = p.closing || {};
  if (clo.title || clo.body) {
    if (clo.title) { lines.push(clo.title); }
    if (clo.body) { lines.push(clo.body); }
    lines.push("");
  }

  return lines.join("\n").trim() + "\n";
}
